import PublicHeader from '@/components/ui/public-header';
import Footer from '@/components/ui/footer';
import { getCompanyFullAddress, getCompanyEmails, getProductName } from '@/lib/config';

export default function PrivacyPage() {
  const productName = getProductName();
  return (
    <div className="flex flex-col min-h-screen">
      <PublicHeader />
      
      <main className="flex-1 bg-surface-marketing">
        <div className="max-w-4xl mx-auto px-4 py-16">
          <h1 className="text-4xl font-bold text-content-heading mb-8">Privacy Policy</h1>
          <p className="text-content-secondary mb-8">Last updated: January 2025</p>
          
          <div className="prose prose-lg max-w-none">
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-content-heading mb-4">Introduction</h2>
              <p className="text-content-body mb-4">
                At {productName}, we take your privacy seriously. This Privacy Policy explains how we collect, use, and protect your personal information when you use our email campaign service.
              </p>
              <p className="text-content-body">
                By using our service, you agree to the collection and use of information in accordance with this policy.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-content-heading mb-4">Information We Collect</h2>
              <h3 className="text-xl font-medium text-content-heading mb-3">Account Information</h3>
              <p className="text-content-body mb-4">
                When you create an account, we collect:
              </p>
              <ul className="list-disc list-inside text-content-body mb-4 space-y-1">
                <li>Name and email address</li>
                <li>Company information</li>
                <li>Billing and payment information</li>
                <li>Account credentials</li>
              </ul>
              
              <h3 className="text-xl font-medium text-content-heading mb-3">Campaign Data</h3>
              <p className="text-content-body mb-4">
                When you use our service, we collect:
              </p>
              <ul className="list-disc list-inside text-content-body mb-4 space-y-1">
                <li>Email contact lists and recipient information</li>
                <li>Email content and templates</li>
                <li>Campaign performance metrics</li>
                <li>Email engagement data (opens, clicks, replies)</li>
              </ul>

              <h3 className="text-xl font-medium text-content-heading mb-3">Automatically Collected Information</h3>
              <p className="text-content-body mb-4">
                We automatically collect:
              </p>
              <ul className="list-disc list-inside text-content-body space-y-1">
                <li>IP address and location data</li>
                <li>Browser type and version</li>
                <li>Device information</li>
                <li>Usage patterns and preferences</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-content-heading mb-4">How We Use Your Information</h2>
              <p className="text-content-body mb-4">
                We use your information to:
              </p>
              <ul className="list-disc list-inside text-content-body space-y-1">
                <li>Provide and improve our email campaign services</li>
                <li>Process payments and manage your account</li>
                <li>Send and track your email campaigns</li>
                <li>Provide customer support</li>
                <li>Analyze service usage and improve our platform</li>
                <li>Comply with legal obligations</li>
                <li>Send important service updates and notifications</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-content-heading mb-4">Information Sharing</h2>
              <p className="text-content-body mb-4">
                We do not sell, trade, or rent your personal information to third parties. We may share your information only in the following circumstances:
              </p>
              <ul className="list-disc list-inside text-content-body space-y-1">
                <li><strong>Service Providers:</strong> With trusted third-party services that help us operate our platform (hosting, payment processing, analytics)</li>
                <li><strong>Legal Requirements:</strong> When required by law or to protect our rights and safety</li>
                <li><strong>Business Transfers:</strong> In the event of a merger, acquisition, or sale of assets</li>
                <li><strong>With Your Consent:</strong> When you explicitly authorize us to share your information</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-content-heading mb-4">Data Security</h2>
              <p className="text-content-body mb-4">
                We implement industry-standard security measures to protect your personal information:
              </p>
              <ul className="list-disc list-inside text-content-body space-y-1">
                <li>Data encryption in transit and at rest</li>
                <li>Regular security audits and monitoring</li>
                <li>Access controls and authentication</li>
                <li>Secure data centers and infrastructure</li>
              </ul>
              <p className="text-content-body mt-4">
                However, no method of transmission over the internet is 100% secure. We cannot guarantee absolute security but will notify you of any significant security breaches.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-content-heading mb-4">Your Rights</h2>
              <p className="text-content-body mb-4">
                You have the right to:
              </p>
              <ul className="list-disc list-inside text-content-body space-y-1">
                <li><strong>Access:</strong> Request copies of your personal information</li>
                <li><strong>Rectify:</strong> Correct inaccurate or incomplete information</li>
                <li><strong>Delete:</strong> Request deletion of your personal information</li>
                <li><strong>Restrict:</strong> Limit how we process your information</li>
                <li><strong>Portability:</strong> Receive your data in a portable format</li>
                <li><strong>Object:</strong> Opt out of certain data processing activities</li>
              </ul>
              <p className="text-content-body mt-4">
                To exercise these rights, contact us at <a href={`mailto:${getCompanyEmails().privacy}`} className="text-link hover:text-link-hover">{getCompanyEmails().privacy}</a>.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-content-heading mb-4">Cookies</h2>
              <p className="text-content-body mb-4">
                We use cookies and similar technologies to:
              </p>
              <ul className="list-disc list-inside text-content-body space-y-1">
                <li>Remember your preferences and settings</li>
                <li>Analyze website traffic and usage patterns</li>
                <li>Improve our services and user experience</li>
                <li>Provide personalized content</li>
              </ul>
              <p className="text-content-body mt-4">
                You can control cookies through your browser settings, but disabling cookies may affect your experience.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-content-heading mb-4">Data Retention</h2>
              <p className="text-content-body">
                We retain your personal information only as long as necessary to provide our services and comply with legal obligations. Account information is typically retained for 3 years after account closure, unless required by law to retain it longer.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-content-heading mb-4">International Data Transfers</h2>
              <p className="text-content-body">
                Your information may be transferred to and processed in countries other than your own. We ensure appropriate safeguards are in place to protect your information in accordance with applicable data protection laws.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-content-heading mb-4">Changes to This Policy</h2>
              <p className="text-content-body">
                We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the new policy on our website and updating the "Last updated" date.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-content-heading mb-4">Contact Us</h2>
              <p className="text-content-body">
                If you have any questions about this Privacy Policy, please contact us at:
              </p>
              <div className="mt-4 text-content-body">
                <p>Email: <a href={`mailto:${getCompanyEmails().privacy}`} className="text-link hover:text-link-hover">{getCompanyEmails().privacy}</a></p>
                <p>Address: {getCompanyFullAddress()}</p>
              </div>
            </section>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}

